import { BodySecondary, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { Close } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { useEffect, useRef } from 'react';

type Props = {
    anchorSelector: string;
    onClose: () => void;
    onOutsideClick: () => void;
    showWidget: boolean;
};

export const HowToWinHeaderPopover = ({ anchorSelector, onClose, onOutsideClick, showWidget }: Props) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const { formatMessage } = useIntl();

    useEffect(() => {
        if (!showWidget) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!(target instanceof Element)) {
                return;
            }

            if (tooltipRef.current?.contains(target)) {
                return;
            }

            onOutsideClick();
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onOutsideClick, showWidget]);

    return (
        <Tooltip
            anchorSelect={anchorSelector}
            className="max-w-[280px] !bg-background-inverse !px-0 !py-0"
            clickable
            isOpen={showWidget}
            place="bottom"
        >
            <div
                className="flex flex-col gap-xs p-sm"
                data-testid="howToWinHeaderPopover"
                onClick={(e) => {
                    e.stopPropagation();
                }}
                ref={tooltipRef}
            >
                <div className="flex items-start justify-between gap-sm">
                    <H5 className="flex-1 text-text-inverse">{formatMessage({ id: 'howToWinHeaderPopover.title' })}</H5>
                    <IconButton
                        aria-label={formatMessage({ id: 'close' })}
                        className="-mr-2xs -mt-2xs shrink-0 text-text-inverse hover:text-text-inverse"
                        data-testid="howToWinHeaderPopoverClose"
                        icon={Close}
                        onClick={onClose}
                        size="xs"
                    />
                </div>
                <BodySecondary className="text-text-inverse opacity-90">
                    <FormattedMessage id="howToWinHeaderPopover.description" />
                </BodySecondary>
            </div>
        </Tooltip>
    );
};
